import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { supabase } from '../../lib/supabase';
import RankingList from './RankingList';

export default function RankingPage() {
  const { session } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [rankingUsers, setRankingUsers] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 40;

  // Calculate pagination values
  const totalPages = Math.ceil(rankingUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = rankingUsers.slice(indexOfFirstUser, indexOfLastUser);

  useEffect(() => {
    const loadRanking = async () => {
      try {
        setLoading(true);
        const { data: ranking, error } = await supabase
          .from('users_ordered_by_elo')
          .select('*')
          .order('elo_rating', { ascending: false });

        if (error) throw error;
        setRankingUsers(ranking || []);
      } catch (error) {
        console.error('Error loading ranking:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRanking();
  }, []);

  return (
    <motion.div 
      className="min-h-screen bg-base-100 pt-16 p-4 relative overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Background decoration */}
      <div className="fixed inset-0 opacity-20 pointer-events-none z-0">
        <img
          src="/images/grid-pattern.svg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="flex flex-col items-center">
          <div className="w-full max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold mb-2 text-primary drop-shadow-glow">{t('ranking.ranking')}</h2>
                <p className="text-base-content/80 max-w-md">
                  {t('ranking.description', 'See the best players ranked by ELO')}
                </p>
              </div>

              {session && (
                <button 
                  onClick={() => window.history.back()}
                  className="btn btn-ghost btn-sm gap-2 text-primary hover:bg-primary/10"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  {t('common.back', 'Back')}
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center min-h-[40vh]">
                <span className="loading loading-spinner loading-lg text-primary"></span>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute -inset-1 bg-primary/30 rounded-box blur-sm opacity-80"></div>
                <div className="relative">
                  <RankingList 
                    users={currentUsers}
                    currentUserId={session?.user?.id}
                    preview={false}
                    startRank={indexOfFirstUser + 1}
                  />
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8 mb-4">
                    <div className="join shadow-xl">
                      <button 
                        className="join-item btn btn-sm bg-base-100 hover:bg-primary/20 text-primary border-primary/30"
                        onClick={() => {
                          setCurrentPage(prev => Math.max(prev - 1, 1));
                          // Scroll to top when changing page
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        disabled={currentPage === 1}
                      >
                        «
                      </button>
                      
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        // Show pages centered around current page
                        let pageOffset = 0;
                        if (currentPage > 3 && totalPages > 5) {
                          pageOffset = currentPage - 3;
                        }
                        if (currentPage > totalPages - 2 && totalPages > 5) {
                          pageOffset = totalPages - 5;
                        }
                        const page = i + 1 + pageOffset;
                        if (page > totalPages) return null;
                        
                        return (
                          <button
                            key={page}
                            className={`join-item btn btn-sm ${currentPage === page 
                              ? 'bg-primary/80 text-base-100 hover:bg-primary' 
                              : 'bg-base-100 hover:bg-primary/20 text-primary border-primary/30'}`}
                            onClick={() => {
                              setCurrentPage(page);
                              // Scroll to top when changing page
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button 
                        className="join-item btn btn-sm bg-base-100 hover:bg-primary/20 text-primary border-primary/30"
                        onClick={() => {
                          setCurrentPage(prev => Math.min(prev + 1, totalPages));
                          // Scroll to top when changing page
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        disabled={currentPage === totalPages}
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="h-20"></div>
    </motion.div>
  );
}
